<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
import FilterButton from './filters/FilterButton.vue'
import type { FilterInputSchema } from '~/composables/simpleFilters'

const props = defineProps<{
  modelValue?: any[]
  schema?: FilterInputSchema[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
  (
    e: 'apply',
    filter: { name: string; operation: string; values: any | any[] },
  ): void
}>()

const appliedFilters = ref(props.modelValue || [])
const filters = computed(() =>
  [...(props.schema || [])].map((item) => ({
    item,
    values: appliedFilters.value.reduce((acc: any, filterItem) => {
      if (filterItem.name === item.name) {
        acc.push(filterItem)
      }

      return acc
    }, []),
  })),
)

const apply = (
  name: string,
  { operation, values }: { operation: string; values: any | any[] },
) => {
  const targetIndex = appliedFilters.value.findIndex(
    (item) => item.name === name && item.operation === operation,
  )

  const newItem = {
    name,
    operation,
    values: Array.isArray(values) ? values : [values],
  }
  if ((props.schema.find(s => s.name === name) as any)?.currency) {
    newItem.values = newItem.values.map(v => +v * 100)
  }

  if (targetIndex >= 0) {
    appliedFilters.value[targetIndex] = newItem
  } else {
    appliedFilters.value = [...appliedFilters.value, newItem]
  }

  emit('update:modelValue', appliedFilters.value)
  emit('apply', { name, operation, values })
}

const remove = ({ name, operation }: FilterInput) => {
  appliedFilters.value = appliedFilters.value.filter(
    (item) => !(item.name === name && item.operation === operation),
  )
  emit('update:modelValue', appliedFilters.value)
}
const clear = () => {
  appliedFilters.value = []
  emit('update:modelValue', appliedFilters.value)
}
const onApply = (submit: Function, close: Function) => {
  submit();
  close()
}
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <FilterButton
      v-for="(filter, i) in appliedFilters"
      :key="filter.name + i"
      :filter="{
        ...filters.find(({ item }) => item.name === filter.name),
        ...filter,
      }"
      @remove="remove(filter)"
    />
    <Popover>
      <template #button>
        <Button variant="gray" icon="i-heroicons-solid-plus">
          Add filter
        </Button>
      </template>
      <template #default="{ close }">
        <TabGroup as="div" vertical class="flex max-h-[280px]">
          <TabList class="border-r py-2 px-0 overflow-y-auto w-48">
            <Tab
              v-for="(filter, i) in filters"
              :key="i"
              v-slot="{ selected }"
              as="template"
            >
              <div
                class="block w-full px-4 py-2 hover:bg-gray-100 text-sm font-medium text-gray-500 cursor-pointer"
                :class="{ 'bg-gray-100 text-gray-800': selected }"
              >
                {{ filter.item.label || filter.item.name }}
              </div>
            </Tab>
          </TabList>
          <TabPanels class="py-5 px-6 w-full overflow-y-scroll">
            <TabPanel v-for="(filter, i) in filters" :key="i">
              <FiltersItem
                :model-value="filter.values"
                :item="filter.item"
                :availableFilters="(filter.item as any).availableFilters || []"
                @update:model-value="apply(filter.item.name, $event)"
              >
                <template #footer="{ submit }">
                  <div class="flex space-x-2 pt-2">
                    <Button variant="gray" @click="close"> Cancel </Button>
                    <Button
                      variant="primary"
                      class="flex-grow"
                      @click="
                        onApply(submit, close)
                      "
                    >
                      Apply Filter
                    </Button>
                  </div>
                </template>
              </FiltersItem>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </template>
    </Popover>
    <Button
      v-if="appliedFilters.length"
      variant="simple"
      icon="i-heroicons-solid-delete"
      @click="clear"
    >
      Clear All
    </Button>
  </div>
</template>
